import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Mail, Lock, User, Eye, EyeOff, ArrowLeft, Home, Heart, MessageCircle, CreditCard, BarChart3, Newspaper, Gift, Sparkles, CheckCircle2, Car, ShieldCheck, Zap, Globe } from "lucide-react";
import { useAuth } from "../App";
import { toast } from "sonner";
import axios from "axios";
import LanguageSelector from "../components/LanguageSelector";
import { useLanguage } from "../contexts/LanguageContext";
import { InstallHelpButtons } from "../components/InstallHelpButtons";

const ACCENT = "#d04260";
const ACCENT_DARK = "#b91c47";
const PARK_ACCENT = "#1E40AF";
const TERMS_VERSION = "2026-09-24";

// Etichetta consenso Termini nelle 5 lingue supportate.
const TERMS_LABEL = {
  it: { before: "Ho letto e accetto i", link: "Termini e Condizioni di Servizio", after: "di Hostland", error: "Devi accettare i Termini e Condizioni per continuare" },
  en: { before: "I have read and accept Hostland's", link: "Terms and Conditions of Service", after: "", error: "You must accept the Terms and Conditions to continue" },
  fr: { before: "J'ai lu et j'accepte les", link: "Conditions Générales de Service", after: "de Hostland", error: "Vous devez accepter les Conditions Générales pour continuer" },
  es: { before: "He leído y acepto los", link: "Términos y Condiciones del Servicio", after: "de Hostland", error: "Debes aceptar los Términos y Condiciones para continuar" },
  de: { before: "Ich habe die", link: "Allgemeinen Geschäftsbedingungen", after: "von Hostland gelesen und akzeptiert", error: "Du musst die Geschäftsbedingungen akzeptieren, um fortzufahren" },
};

// Dichiarazione di maggiore età — obbligatoria alla registrazione.
const AGE_LABEL = {
  it: { text: "Dichiaro di essere maggiorenne (almeno 18 anni).", error: "Devi dichiarare di essere maggiorenne per registrarti" },
  en: { text: "I declare that I am of legal age (at least 18 years old).", error: "You must declare that you are of legal age to register" },
  fr: { text: "Je déclare être majeur (au moins 18 ans).", error: "Vous devez déclarer être majeur pour vous inscrire" },
  es: { text: "Declaro ser mayor de edad (al menos 18 años).", error: "Debes declarar ser mayor de edad para registrarte" },
  de: { text: "Ich erkläre, dass ich volljährig bin (mindestens 18 Jahre).", error: "Du musst erklären, dass du volljährig bist, um dich zu registrieren" },
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, register, user } = useAuth();
  const { t, lang } = useLanguage();

  // PWA scope-aware login: su /control/login e /dashboard/login il redirect post-login
  // resta DENTRO lo scope (niente ritorno alla root). Il parametro ?next= è accettato
  // solo se è un percorso interno che inizia con lo scope corrente; altrimenti si usa
  // /control o /dashboard come destinazione di default. Su /login normale nulla cambia.
  const _loginScope = location.pathname.startsWith("/control") ? "/control"
    : location.pathname.startsWith("/dashboard") ? "/dashboard"
    : null;
  const _scopedTarget = (() => {
    if (!_loginScope) return null;
    const n = searchParams.get("next") || "";
    if (n.startsWith(_loginScope) && !n.startsWith("//") && n.indexOf("://") === -1) return n;
    return _loginScope;
  })();
  const resolveRedirect = (u) => {
    if (_loginScope) return _scopedTarget;
    const defaultHome = u?.user_type === "guest" ? "/my" : "/dashboard";
    return location.state?.from?.pathname || defaultHome;
  };

  const asParam = (searchParams.get("as") || "").toLowerCase();
  const modeParam = (searchParams.get("mode") || "").toLowerCase();
  const planParam = (searchParams.get("plan") || "").toLowerCase();
  const initialMode =
    asParam === "guest" ? "register-guest"
    : asParam === "host" ? "register-host"
    : asParam === "parkhost" ? "register-parkhost"
    : modeParam === "signup" ? "register-host"
    : ["guestbook_only", "monthly", "yearly"].includes(planParam) ? "register-host"  // link diretto dalla pricing
    : "login";

  const [mode, setMode] = useState(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: (new URLSearchParams(window.location.search).get("email") || "").trim(), password: "", name: "" });
  // Selezione UI del piano: "monthly" | "full" (annuale) | "guestbook_only".
  // Il backend accetta solo plan_type=full|guestbook_only, quindi mappiamo:
  //   monthly → plan_type=full + billing_preference=monthly
  //   full    → plan_type=full + billing_preference=yearly (default)
  //   guestbook_only → plan_type=guestbook_only (billing ignorato)
  // Se l'URL contiene `?plan=guestbook_only|monthly|yearly` pre-selezioniamo.
  const _planParam = (searchParams.get("plan") || "").toLowerCase();
  const _initialPlan =
    _planParam === "guestbook_only" ? "guestbook_only"
    : _planParam === "monthly" ? "monthly"
    : "full";
  const [planType, setPlanType] = useState(_initialPlan);
  // Vieni da Noi migration: pre-fill code from ?code= URL param (link in Brevo email).
  const [discountCode, setDiscountCode] = useState((searchParams.get("code") || "").trim().toUpperCase());
  const [showDiscountField, setShowDiscountField] = useState(!!(searchParams.get("code") || "").trim());
  // Iscrizione newsletter Vita da Host — opt-in NON pre-spuntato (consenso GDPR valido).
  // L'email viene iscritta alla lista Brevo solo se l'utente spunta la casella.
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  // Accettazione obbligatoria dei Termini e Condizioni (non pre-spuntata).
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  // Dichiarazione maggiore età — obbligatoria.
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [ageError, setAgeError] = useState(false);

  useEffect(() => {
    if (user) {
      navigate(resolveRedirect(user), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const loggedUser = await login(formData.email, formData.password);
        toast.success("Accesso effettuato!");
        navigate(resolveRedirect(loggedUser), { replace: true });
      } else {
        // Accettazione Termini obbligatoria per qualsiasi registrazione.
        if (!termsAccepted) {
          setTermsError(true);
          setLoading(false);
          return;
        }
        // Dichiarazione di maggiore età obbligatoria.
        if (!ageConfirmed) {
          setAgeError(true);
          setLoading(false);
          return;
        }
        const isParkHostSignup = mode === "register-parkhost";
        const userType = mode === "register-guest" ? "guest" : "host";
        // Mappa la selezione UI a plan_type + billing_preference per il backend.
        // monthly UI → plan_type=full, billing=monthly
        // full UI    → plan_type=full, billing=yearly (default)
        // guestbook_only UI → plan_type=guestbook_only, billing irrilevante
        const uiPlanType = mode === "register-host" ? (
          planType === "guestbook_only" ? "guestbook_only" : "full"
        ) : null;
        const uiBillingPref = mode === "register-host" && planType === "monthly" ? "monthly" : "yearly";
        const res = await register(formData.email, formData.password, formData.name, userType, mode === "register-host" ? (discountCode.trim() || null) : null, uiPlanType, mode === "register-host" ? uiBillingPref : null, true, TERMS_VERSION);
        // Iscrizione newsletter Vita da Host (fire-and-forget) se la spunta è attiva.
        // Fatta SUBITO dopo il register — indipendente dalla verifica email — così anche
        // gli utenti che non completano la verifica finiscono comunque nella lista Brevo.
        if (newsletterOptIn && formData.email) {
          try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
              email: formData.email,
              first_name: formData.name || null,
              source: `hostland-signup-${userType}${isParkHostSignup ? "-parkhost" : ""}`,
            });
          } catch { /* silent: non blocca la registrazione */ }
        }
        // Toast personalizzato in base al tier del codice riscattato.
        // I codici VDH/GB/etc. hanno durata diversa (30gg, 90gg, 365gg, 730gg,
        // a vita) e mostrare "1 anno" per un codice da 1 mese confondeva l'utente.
        if (res?.vdn_grant?.granted) {
          const TIER_LABELS = {
            lifetime:     "a vita",
            "2y":         "2 anni",
            "2y_multi":   "2 anni",
            "1y":         "1 anno",
            "3m":         "3 mesi",
            guestbook_1y: "1 anno di Welcome Book",
            guestbook_3m: "3 mesi di Welcome Book",
            parkhost_1y:  "1 anno di ParkHost",
            vdh_1m:       "1 mese",
            vdh_3m:       "3 mesi",
            vdh_1y:       "1 anno",
          };
          const dur = TIER_LABELS[res.vdn_grant.tier] || "gratis";
          const productName = res.vdn_grant.tier === "parkhost_1y" ? "ParkHost" : "Hostland";
          toast.success(`🎁 Codice attivato — ${dur} di ${productName}!`);
        } else if (mode === "register-host" && discountCode.trim() && res?.vdn_grant && !res.vdn_grant.granted) {
          toast.error("Codice sconto non valido — la registrazione è stata comunque completata");
        }
        if (res?.needs_verification) {
          if (isParkHostSignup) {
            try { localStorage.setItem("hostland_pending_parkhost_enroll", "1"); } catch {}
          }
          setMode("verify-sent");
        } else {
          if (isParkHostSignup) {
            try {
              const tokenStored = localStorage.getItem("token");
              await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/parkhost/enroll`,
                {},
                { headers: { Authorization: `Bearer ${tokenStored}` } },
              );
              toast.success("Account ParkHost creato! 30 giorni gratis 🎉");
              navigate("/dashboard/park", { replace: true });
              return;
            } catch { /* fall through */ }
          }
          toast.success("Account creato con successo!");
          navigate(resolveRedirect({ user_type: userType }), { replace: true });
        }
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "";
      if (detail === "EMAIL_NOT_VERIFIED") {
        toast.error("Devi prima confermare la tua email! Controlla la casella di posta.");
      } else {
        toast.error(detail || "Errore durante l'accesso");
      }
    } finally {
      setLoading(false);
    }
  };

  const isRegister = mode === "register-host" || mode === "register-guest" || mode === "register-parkhost";
  const isParkHost = mode === "register-parkhost";

  const heroCopy = {
    "login": { title: "Bentornato su Hostland", sub: "Accedi al tuo account host, ospite o ParkHost." },
    "choose": { title: "Chi sei oggi?", sub: "Scegli il tuo profilo per iniziare a usare Hostland." },
    "register-host": { title: "Diventa Host in 2 minuti", sub: "Il tuo sito, il Welcome Book Digitale e il QR code — subito operativi." },
    "register-guest": { title: "Trova la casa perfetta", sub: "Salva preferiti, contatta host, accumula punti fedeltà." },
    "register-parkhost": { title: "Monetizza il tuo posto auto", sub: "Il tuo garage o box vale più di quanto pensi. Attiva ParkHost." },
    "forgot": { title: "Recupera l'accesso", sub: "Ti mandiamo le istruzioni via email in pochi secondi." },
    "verify-sent": { title: "Controlla la tua email", sub: "Ti abbiamo mandato un link di conferma." },
  };
  const currentCopy = heroCopy[mode] || heroCopy["login"];

  return (
    <div className="min-h-screen bg-[#F5F5F7] relative overflow-hidden">
      <Helmet>
        <title>Accedi o Registrati — Hostland by Vita da Host</title>
        <meta name="description" content="Accedi a Hostland o crea il tuo account gratuito: piattaforma italiana per host, property manager, viaggiatori e ParkHost. Landing page, Welcome Book Digitale, QR code, prenotazioni dirette senza commissioni." />
        <link rel="canonical" href="https://hostland.vitadahost.it/login" />
      </Helmet>

      {/* Ambient background orbs */}
      <motion.div aria-hidden animate={{ x: [0, 40, 0], y: [0, -20, 0] }} transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        className="fixed -top-32 -right-32 w-[500px] h-[500px] rounded-full blur-3xl opacity-40 pointer-events-none"
        style={{ background: `radial-gradient(circle, ${ACCENT}55, transparent 70%)` }} />
      <motion.div aria-hidden animate={{ x: [0, -40, 0], y: [0, 20, 0] }} transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
        className="fixed -bottom-32 -left-32 w-[500px] h-[500px] rounded-full blur-3xl opacity-30 pointer-events-none"
        style={{ background: "radial-gradient(circle, #F59E0B55, transparent 70%)" }} />

      <div className="relative z-10 min-h-screen grid lg:grid-cols-2">

        {/* ═══ LEFT PANEL — showcase (hidden on mobile) ═══ */}
        <div className="hidden lg:flex flex-col justify-between p-10 xl:p-14 relative overflow-hidden" style={{ background: "linear-gradient(160deg, #0f172a 0%, #1e293b 55%, #0f172a 100%)" }}>
          <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.15) 0 1px, transparent 2px), radial-gradient(circle at 70% 70%, rgba(255,255,255,0.1) 0 1px, transparent 2px)", backgroundSize: "60px 60px" }} />

          <div className="relative">
            <a href="/" className="inline-flex items-center gap-3 group" data-testid="brand-link">
              <img src="/hostland-logo.png" alt="Hostland" className="w-10 h-10 rounded-lg object-cover" />
              <span className="text-white font-black text-xl tracking-tight">Hostland</span>
            </a>
          </div>

          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={mode}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              >
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase text-white/80 bg-white/10 border border-white/10 mb-5">
                  <Sparkles className="w-3 h-3" /> Zero commissioni · Made in Italy
                </span>
                <h1 className="text-white text-4xl xl:text-5xl font-black tracking-tighter leading-[1.05]">
                  {currentCopy.title}
                </h1>
                <p className="text-white/70 text-base mt-3 font-medium max-w-md leading-relaxed">
                  {currentCopy.sub}
                </p>
              </motion.div>
            </AnimatePresence>

            <div className="grid grid-cols-2 gap-3 mt-10 max-w-md">
              {[
                { icon: <Zap className="w-4 h-4" />, label: "Setup in 2 minuti" },
                { icon: <ShieldCheck className="w-4 h-4" />, label: "Pagamenti Stripe®" },
                { icon: <Globe className="w-4 h-4" />, label: "5 lingue automatiche" },
                { icon: <Sparkles className="w-4 h-4" />, label: "Welcome Book incluso" },
              ].map((f, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.08 }}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/90">
                  <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${ACCENT}30`, color: ACCENT }}>{f.icon}</span>
                  <span className="text-xs font-bold">{f.label}</span>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="relative text-white/50 text-xs font-medium">
            <p>© 2026 Hostland by Vita da Host · <a href="/privacy" className="underline hover:text-white">Privacy</a> · <a href="/terms" className="underline hover:text-white">Termini</a></p>
          </div>
        </div>

        {/* ═══ RIGHT PANEL — form ═══ */}
        <div className="flex flex-col p-5 sm:p-8 lg:p-10 xl:p-14 min-h-screen">
          {/* Mobile brand + back button */}
          <div className="flex items-center justify-between mb-6">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => mode === "choose" || mode === "login" ? navigate("/") : setMode(isRegister ? "choose" : "login")}
              className="flex items-center gap-2 text-zinc-500 hover:text-zinc-900 transition-colors font-semibold text-sm"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">{mode === "login" || mode === "choose" ? "Home" : "Indietro"}</span>
            </motion.button>

            <a href="/" className="lg:hidden inline-flex items-center gap-2">
              <img src="/hostland-logo.png" alt="Hostland" className="w-8 h-8 rounded-lg object-cover" />
              <span className="font-black text-zinc-900 tracking-tight">Hostland</span>
            </a>

            <LanguageSelector variant="compact" />
          </div>

          {/* Mobile-only hero title (from LEFT panel) */}
          <div className="lg:hidden mb-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={mode}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.3 }}
              >
                <h1 className="text-2xl sm:text-3xl font-black text-zinc-900 tracking-tighter leading-tight">{currentCopy.title}</h1>
                <p className="text-zinc-500 text-sm font-medium mt-1.5">{currentCopy.sub}</p>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex-1 flex items-center">
            <div className="w-full max-w-md mx-auto">
              <AnimatePresence mode="wait">
                {/* ============ LOGIN MODE ============ */}
                {mode === "login" && (
                  <motion.div key="login" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="w-full">
                    <h2 className="hidden lg:block text-3xl font-black text-zinc-900 tracking-tight mb-1" data-testid="login-title">Accedi</h2>
                    <p className="hidden lg:block text-zinc-500 mb-6 font-medium text-sm">Entra nel tuo account Hostland</p>

                    <form onSubmit={handleSubmit} className="space-y-3">
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="la-tua@email.com" required autoComplete="email"
                          className="w-full bg-white border border-zinc-200 rounded-2xl pl-11 pr-4 py-3.5 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/20 focus:border-zinc-900 font-medium text-sm transition-colors"
                          data-testid="email-input" />
                      </div>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <input type={showPassword ? "text" : "password"} value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          placeholder="Password" required minLength={6} autoComplete="current-password"
                          className="w-full bg-white border border-zinc-200 rounded-2xl pl-11 pr-11 py-3.5 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/20 focus:border-zinc-900 font-medium text-sm transition-colors"
                          data-testid="password-input" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 transition-colors" aria-label="Mostra/nascondi password">
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <motion.button whileTap={{ scale: 0.98 }} whileHover={{ scale: 1.01 }} type="submit" disabled={loading}
                        className="w-full text-white rounded-2xl py-3.5 font-black text-sm transition-all disabled:opacity-50 mt-2 shadow-lg"
                        style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)` }}
                        data-testid="submit-btn">
                        {loading ? <span className="flex items-center justify-center gap-2"><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Accesso in corso...</span> : "Accedi"}
                      </motion.button>
                    </form>

                    {_loginScope && (
                      <div className="mt-5 pt-5 border-t border-zinc-100">
                        <p className="text-center text-[11px] font-bold uppercase tracking-wider text-zinc-400 mb-2.5">
                          Installa Hostland come app
                        </p>
                        <InstallHelpButtons />
                      </div>
                    )}

                    <button onClick={() => setMode("forgot")} className="block mx-auto mt-4 text-xs text-zinc-500 hover:text-zinc-900 font-semibold transition-colors" data-testid="forgot-password-btn">
                      Password dimenticata?
                    </button>

                    <div className="flex items-center gap-3 my-6">
                      <div className="flex-1 h-px bg-zinc-200" />
                      <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Nuovo su Hostland?</span>
                      <div className="flex-1 h-px bg-zinc-200" />
                    </div>

                    <button
                      onClick={() => setMode("choose")}
                      className="w-full rounded-2xl py-3.5 font-black text-sm border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white transition-all"
                      data-testid="toggle-auth-btn"
                    >
                      Crea account gratis
                    </button>
                  </motion.div>
                )}

                {/* ============ VERIFY SENT ============ */}
                {mode === "verify-sent" && (
                  <motion.div key="verify-sent" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Mail className="w-8 h-8 text-emerald-500" />
                      </div>
                      <h2 className="text-xl font-black text-zinc-900 mb-2">Controlla la tua email!</h2>
                      <p className="text-zinc-500 text-sm font-medium mb-2">Abbiamo inviato un'email di conferma a:</p>
                      <p className="text-zinc-900 font-black text-sm mb-4">{formData.email}</p>
                      <p className="text-zinc-500 text-xs leading-relaxed mb-6">Clicca il link nell'email per attivare il tuo account. Controlla anche Spam o Promozioni.</p>
                      <button onClick={() => setMode("login")} className="text-sm font-black text-zinc-900 hover:underline" data-testid="back-to-login-btn">
                        Torna al Login
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* ============ FORGOT PASSWORD ============ */}
                {mode === "forgot" && (
                  <motion.div key="forgot" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="w-full">
                    <h2 className="hidden lg:block text-3xl font-black text-zinc-900 tracking-tight mb-1">Recupera password</h2>
                    <p className="hidden lg:block text-zinc-500 mb-6 font-medium text-sm">Ti invieremo le istruzioni via email</p>
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      setLoading(true);
                      try {
                        const API = process.env.REACT_APP_BACKEND_URL + "/api";
                        await fetch(`${API}/auth/forgot-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: formData.email }) });
                        toast.success("Se l'email esiste, riceverai le istruzioni per il reset!");
                        setMode("login");
                      } catch { toast.error("Errore, riprova"); }
                      finally { setLoading(false); }
                    }} className="space-y-3">
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="la-tua@email.com" required
                          className="w-full bg-white border border-zinc-200 rounded-2xl pl-11 pr-4 py-3.5 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/20 focus:border-zinc-900 font-medium text-sm"
                          data-testid="forgot-email-input" />
                      </div>
                      <motion.button whileTap={{ scale: 0.98 }} type="submit" disabled={loading}
                        className="w-full text-white rounded-2xl py-3.5 font-black text-sm transition-all disabled:opacity-50 shadow-lg"
                        style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)` }}
                        data-testid="forgot-submit-btn">
                        {loading ? "Invio..." : "Invia istruzioni"}
                      </motion.button>
                    </form>
                    <button onClick={() => setMode("login")} className="block mx-auto mt-4 text-xs text-zinc-500 hover:text-zinc-900 font-semibold transition-colors">
                      Torna al login
                    </button>
                  </motion.div>
                )}

                {/* ============ CHOOSE ROLE ============ */}
                {mode === "choose" && (
                  <motion.div key="choose" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="w-full">
                    <h2 className="hidden lg:block text-3xl font-black text-zinc-900 tracking-tight mb-1" data-testid="choose-title">Crea account</h2>
                    <p className="hidden lg:block text-zinc-500 mb-6 font-medium text-sm">Scegli il tuo profilo</p>

                    <div className="space-y-3">
                      {[
                        { role: "guest", label: "Sono un viaggiatore", desc: "Cerco alloggi, salvo preferiti, contatto host direttamente.", icon: <User className="w-6 h-6" />, color: "#2563eb", bg: "#eff6ff", tags: [{ icon: <Heart className="w-2.5 h-2.5" />, label: "Preferiti" }, { icon: <MessageCircle className="w-2.5 h-2.5" />, label: "Messaggi" }, { icon: <Gift className="w-2.5 h-2.5" />, label: "Fedeltà" }], testid: "choose-guest-btn", target: "register-guest" },
                        { role: "host", label: "Sono un Host o PM", desc: "Gestisco strutture, creo landing e attivo il GuestBook.", icon: <Home className="w-6 h-6" />, color: ACCENT, bg: `${ACCENT}10`, badge: "HOST", tags: [{ icon: <Home className="w-2.5 h-2.5" />, label: "Strutture" }, { icon: <BarChart3 className="w-2.5 h-2.5" />, label: "Stats" }, { icon: <Newspaper className="w-2.5 h-2.5" />, label: "Blog" }, { icon: <CreditCard className="w-2.5 h-2.5" />, label: "Pagamenti" }], testid: "choose-host-btn", target: "register-host" },
                        { role: "parkhost", label: "Sono un ParkHost", desc: "Affitto il mio posto auto, box o garage. €4,99/anno dopo il trial.", icon: <Car className="w-6 h-6" />, color: PARK_ACCENT, bg: `${PARK_ACCENT}12`, badge: "PARKHOST", tags: [{ label: "Landing parcheggio" }, { label: "Contatti diretti" }, { label: "Pin blu" }], testid: "choose-parkhost-btn", target: "register-parkhost" },
                      ].map((card, i) => (
                        <motion.button
                          key={card.role}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.06 }}
                          whileTap={{ scale: 0.98 }}
                          whileHover={{ y: -2, boxShadow: `0 12px 30px -12px ${card.color}55` }}
                          onClick={() => setMode(card.target)}
                          className="w-full p-4 sm:p-5 rounded-2xl border-2 transition-all text-left relative overflow-hidden bg-white"
                          style={{ borderColor: card.role === "guest" ? "#e4e4e7" : card.color }}
                          data-testid={card.testid}
                        >
                          {card.badge && (
                            <div className="absolute top-0 right-0 text-white text-[9px] font-black px-2 py-0.5 rounded-bl-lg" style={{ background: card.color }}>{card.badge}</div>
                          )}
                          <div className="flex items-start gap-3 sm:gap-4">
                            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: card.bg, color: card.color }}>
                              {card.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-black text-zinc-900 text-sm sm:text-base">{card.label}</p>
                              <p className="text-[11px] sm:text-xs text-zinc-500 mt-1 leading-relaxed">{card.desc}</p>
                              <div className="flex flex-wrap gap-1.5 mt-2.5">
                                {card.tags.map((tag, ti) => (
                                  <span key={ti} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold" style={{ background: card.bg, color: card.color }}>
                                    {tag.icon}{tag.label}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </motion.button>
                      ))}
                    </div>

                    <p className="text-center mt-6 text-zinc-500 font-medium text-sm">
                      Hai già un account?{" "}
                      <button onClick={() => setMode("login")} className="text-zinc-900 font-black hover:underline" data-testid="go-login-btn">Accedi</button>
                    </p>
                  </motion.div>
                )}

                {/* ============ REGISTER FORM ============ */}
                {isRegister && (
                  <motion.div key="register" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="w-full">
                    <h2 className="hidden lg:block text-3xl font-black text-zinc-900 tracking-tight mb-1" data-testid="register-title">
                      {isParkHost ? "Registrati come ParkHost" : mode === "register-host" ? "Registrati come Host" : "Registrati come Ospite"}
                    </h2>
                    <p className="hidden lg:block text-zinc-500 mb-6 font-medium text-sm">
                      {isParkHost ? "Attiva il trial 30 giorni e pubblica il tuo parcheggio"
                        : mode === "register-host" ? "Crea le landing delle tue strutture in 2 minuti"
                        : "Salva preferiti e contatta host senza commissioni"}
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-3">
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Il tuo nome" required autoComplete="name"
                          className="w-full bg-white border border-zinc-200 rounded-2xl pl-11 pr-4 py-3.5 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/20 focus:border-zinc-900 font-medium text-sm transition-colors"
                          data-testid="name-input" />
                      </div>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="la-tua@email.com" required autoComplete="email"
                          className="w-full bg-white border border-zinc-200 rounded-2xl pl-11 pr-4 py-3.5 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/20 focus:border-zinc-900 font-medium text-sm transition-colors"
                          data-testid="email-input" />
                      </div>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <input type={showPassword ? "text" : "password"} value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          placeholder="Password (min 6 caratteri)" required minLength={6} autoComplete="new-password"
                          className="w-full bg-white border border-zinc-200 rounded-2xl pl-11 pr-11 py-3.5 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/20 focus:border-zinc-900 font-medium text-sm transition-colors"
                          data-testid="password-input" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 transition-colors" aria-label="Mostra/nascondi password">
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>

                      {/* password strength */}
                      {formData.password && (
                        <div className="flex items-center gap-1.5 pt-1">
                          {[1, 2, 3, 4].map(n => {
                            const strength = (formData.password.length >= 6 ? 1 : 0) + (formData.password.length >= 10 ? 1 : 0) + (/[A-Z]/.test(formData.password) ? 1 : 0) + (/\d|[!@#$%^&*]/.test(formData.password) ? 1 : 0);
                            return <div key={n} className={`flex-1 h-1 rounded-full transition-colors ${n <= strength ? (strength <= 1 ? "bg-red-400" : strength <= 2 ? "bg-amber-400" : strength <= 3 ? "bg-lime-400" : "bg-emerald-500") : "bg-zinc-200"}`} />;
                          })}
                        </div>
                      )}

                      {/* Plan selector (host only) — 3 opzioni: Mensile, Annuale, Solo Welcome */}
                      {mode === "register-host" && (
                        <div className="pt-2">
                          <p className="text-xs font-black uppercase tracking-widest text-zinc-500 mb-1 text-center">Scegli il tuo piano</p>
                          <p className="text-[10px] text-zinc-400 text-center mb-2 font-medium">Lo attivi (e paghi) solo se vorrai, al termine dei 30 giorni</p>
                          <div className="grid grid-cols-3 gap-1.5" data-testid="plan-type-selector">
                            <button
                              type="button"
                              onClick={() => setPlanType("monthly")}
                              className={`relative rounded-2xl border-2 p-2.5 text-left transition-all ${planType === "monthly" ? "border-[#d04260] bg-[#d04260]/5 shadow-md" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
                              data-testid="plan-monthly-btn"
                            >
                              {planType === "monthly" && (
                                <span className="absolute -top-2 -right-2 bg-[#d04260] text-white text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full">Scelto</span>
                              )}
                              <div className="flex items-center gap-1 mb-1">
                                <Globe className="w-3.5 h-3.5 text-[#d04260]" />
                                <span className="text-[10px] font-black text-zinc-900 uppercase tracking-tight">Mensile</span>
                              </div>
                              <p className="text-[9px] text-zinc-500 font-medium leading-tight mb-1">Sito + Welcome — flessibile</p>
                              <div className="flex items-baseline gap-0.5">
                                <span className="text-base font-black text-zinc-900">€9,90</span>
                                <span className="text-[9px] text-zinc-500 font-bold">/mese</span>
                              </div>
                            </button>
                            <button
                              type="button"
                              onClick={() => setPlanType("full")}
                              className={`relative rounded-2xl border-2 p-2.5 text-left transition-all ${planType === "full" ? "border-[#d04260] bg-[#d04260]/5 shadow-md" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
                              data-testid="plan-full-btn"
                            >
                              <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#d04260] to-[#b91c47] text-white text-[8px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap">Consigliato</span>
                              {planType === "full" && (
                                <span className="absolute -top-2 -right-2 bg-emerald-500 text-white text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full">✓</span>
                              )}
                              <div className="flex items-center gap-1 mb-1">
                                <Globe className="w-3.5 h-3.5 text-[#d04260]" />
                                <span className="text-[10px] font-black text-zinc-900 uppercase tracking-tight">Annuale</span>
                              </div>
                              <p className="text-[9px] text-zinc-500 font-medium leading-tight mb-1">Sito + Welcome — risparmi 50%</p>
                              <div className="flex items-baseline gap-0.5">
                                <span className="text-base font-black text-zinc-900">€59,90</span>
                                <span className="text-[9px] text-zinc-500 font-bold">/anno</span>
                              </div>
                            </button>
                            <button
                              type="button"
                              onClick={() => setPlanType("guestbook_only")}
                              className={`relative rounded-2xl border-2 p-2.5 text-left transition-all ${planType === "guestbook_only" ? "border-amber-500 bg-amber-50 shadow-md" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
                              data-testid="plan-guestbook-only-btn"
                            >
                              {planType === "guestbook_only" && (
                                <span className="absolute -top-2 -right-2 bg-amber-500 text-white text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full">Scelto</span>
                              )}
                              <div className="flex items-center gap-1 mb-1">
                                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                                <span className="text-[10px] font-black text-zinc-900 uppercase tracking-tight">Welcome Book</span>
                              </div>
                              <p className="text-[9px] text-zinc-500 font-medium leading-tight mb-1">Fino a 3 Welcome Book · poi €9,90 l'uno</p>
                              <div className="flex items-baseline gap-0.5">
                                <span className="text-base font-black text-zinc-900">€35,90</span>
                                <span className="text-[9px] text-zinc-500 font-bold">/anno</span>
                              </div>
                            </button>
                          </div>
                          <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2.5" data-testid="no-payment-now-banner">
                            <div className="flex items-start gap-2">
                              <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                              <div className="text-left">
                                <p className="text-[12px] font-black text-emerald-800 leading-snug">Oggi non paghi nulla — e non serve la carta di credito.</p>
                                <p className="text-[11px] text-emerald-700 leading-snug mt-1">
                                  I primi <span className="font-bold">30 giorni sono in omaggio</span>. Al termine <span className="font-bold">decidi tu</span> se attivare l'abbonamento: nessun addebito automatico oggi, nessuno scherzo. Solo trasparenza.
                                </p>
                                <p className="text-[10px] text-emerald-700/90 leading-snug mt-1" data-testid="renewal-notice">
                                  Quando sceglierai di attivare, il piano si rinnova automaticamente alla scadenza alla stessa cifra (€9,90/mese o €59,90/anno) salvo disdetta, che puoi fare online in ogni momento da Profilo → Gestisci abbonamento.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Discount code field (host only) */}
                      {mode === "register-host" && (
                        <div className="pt-2">
                          {!showDiscountField ? (
                            <div className="flex items-center gap-2 justify-center">
                              <span className="text-xs font-medium text-zinc-500">Hai un codice sconto?</span>
                              <button
                                type="button"
                                onClick={() => setShowDiscountField(true)}
                                className="inline-flex items-center gap-1.5 rounded-full border border-yellow-400 bg-yellow-50 hover:bg-yellow-100 px-3 py-1.5 text-yellow-800 font-bold text-xs tracking-tight transition-colors"
                                data-testid="show-discount-code-btn"
                              >
                                <Gift className="w-3.5 h-3.5" /> Inseriscilo
                              </button>
                            </div>
                          ) : (
                            <div className="rounded-2xl border-2 border-yellow-400 bg-yellow-50 p-4">
                              <label className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-yellow-800 mb-2">
                                <Gift className="w-4 h-4" /> Inserisci il codice sconto
                              </label>
                              <input
                                type="text"
                                value={discountCode}
                                onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                                placeholder="XXXX-XXXX-XXXX"
                                className="w-full bg-white border-2 border-yellow-300 rounded-xl px-4 py-4 text-zinc-900 font-mono font-black text-lg md:text-xl tracking-[0.15em] text-center focus:outline-none focus:ring-4 focus:ring-yellow-400/40 focus:border-yellow-500 uppercase placeholder:tracking-normal placeholder:text-yellow-300 placeholder:font-normal"
                                data-testid="discount-code-input"
                              />
                              <p className="text-xs text-yellow-800 mt-2.5 leading-relaxed font-medium">
                                Inserisci il tuo codice sconto per ottenere il tuo periodo gratuito senza nessun addebito.
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Newsletter opt-in Vita da Host — pre-spuntato per default */}
                      <label className="flex items-start gap-2.5 pt-1 cursor-pointer select-none group" data-testid="newsletter-optin-label">
                        <input
                          type="checkbox"
                          checked={newsletterOptIn}
                          onChange={(e) => setNewsletterOptIn(e.target.checked)}
                          className="mt-0.5 w-4 h-4 rounded border-zinc-300 text-[#d04260] focus:ring-2 focus:ring-[#d04260]/30 cursor-pointer shrink-0"
                          data-testid="newsletter-optin-checkbox"
                        />
                        <span className="text-[11px] text-zinc-500 leading-relaxed font-medium">
                          <span className="text-zinc-900 font-bold">Iscrivimi alla newsletter di Vita da Host</span> — ti informa su tutte le news del mondo degli affitti brevi, novità normative e consigli per host e viaggiatori.
                          <span className="block text-zinc-400 text-[10px] mt-0.5">Puoi disiscriverti in 1 click da qualsiasi email.</span>
                        </span>
                      </label>

                      {/* Dichiarazione maggiore età — obbligatoria */}
                      {(() => {
                        const AL = AGE_LABEL[lang] || AGE_LABEL.it;
                        return (
                          <div className="pt-0.5">
                            <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="age-confirm-label">
                              <input
                                type="checkbox"
                                checked={ageConfirmed}
                                onChange={(e) => { setAgeConfirmed(e.target.checked); if (e.target.checked) setAgeError(false); }}
                                className="mt-0.5 w-4 h-4 rounded border-zinc-300 text-[#d04260] focus:ring-2 focus:ring-[#d04260]/30 cursor-pointer shrink-0"
                                data-testid="age-confirm-checkbox"
                              />
                              <span className="text-[11px] text-zinc-600 leading-relaxed font-medium">{AL.text}</span>
                            </label>
                            {ageError && (
                              <p className="text-[11px] text-red-500 font-semibold mt-1 pl-6" data-testid="age-error">{AL.error}</p>
                            )}
                          </div>
                        );
                      })()}

                      {/* Accettazione Termini e Condizioni — obbligatoria */}
                      {(() => {
                        const TL = TERMS_LABEL[lang] || TERMS_LABEL.it;
                        return (
                          <div className="pt-0.5">
                            <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="terms-accept-label">
                              <input
                                type="checkbox"
                                checked={termsAccepted}
                                onChange={(e) => { setTermsAccepted(e.target.checked); if (e.target.checked) setTermsError(false); }}
                                className="mt-0.5 w-4 h-4 rounded border-zinc-300 text-[#d04260] focus:ring-2 focus:ring-[#d04260]/30 cursor-pointer shrink-0"
                                data-testid="terms-accept-checkbox"
                              />
                              <span className="text-[11px] text-zinc-600 leading-relaxed font-medium">
                                {TL.before}{" "}
                                <a href="/terms" target="_blank" rel="noopener noreferrer" className="font-bold text-[#d04260] hover:underline" data-testid="terms-link">{TL.link}</a>
                                {TL.after ? ` ${TL.after}` : ""}
                              </span>
                            </label>
                            {termsError && (
                              <p className="text-[11px] text-red-500 font-semibold mt-1 pl-6" data-testid="terms-error">{TL.error}</p>
                            )}
                          </div>
                        );
                      })()}

                      <motion.button whileTap={{ scale: 0.98 }} whileHover={{ scale: 1.01 }} type="submit" disabled={loading || !termsAccepted || !ageConfirmed}
                        className="w-full text-white rounded-2xl py-3.5 font-black text-sm transition-all disabled:opacity-50 mt-3 shadow-lg"
                        style={{
                          background: isParkHost ? `linear-gradient(135deg, ${PARK_ACCENT}, #0EA5E9)`
                            : mode === "register-host" ? `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`
                            : "linear-gradient(135deg, #2563eb, #1d4ed8)"
                        }}
                        data-testid="submit-btn">
                        {loading ? <span className="flex items-center justify-center gap-2"><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Attendere...</span>
                          : isParkHost ? "Attiva ParkHost · Trial 30 giorni"
                          : mode === "register-host" ? "Crea account Host · gratis"
                          : "Crea account Ospite · gratis"}
                      </motion.button>
                    </form>

                    <div className="flex flex-wrap items-center justify-center gap-2 mt-4 text-[10px] font-bold text-zinc-500">
                      <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> 30 giorni gratis</span>
                      <span className="text-zinc-300">·</span>
                      <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Nessuna carta richiesta</span>
                      <span className="text-zinc-300">·</span>
                      <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Cancella quando vuoi</span>
                    </div>

                    <p className="text-center mt-5 text-zinc-500 font-medium text-sm">
                      Hai già un account?{" "}
                      <button onClick={() => setMode("login")} className="text-zinc-900 font-black hover:underline" data-testid="go-login-btn">Accedi</button>
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
